import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const PricingSection = () => {
  const plans = [
    {
      name: "🌱 Starter Plan",
      price: "$99",
      period: "mo",
      retainer: "+ $500 retainer",
      subtitle: "For Entrepreneurs, Solopreneurs & Small Teams testing AI automation",
      features: [
        "🤖 Access to 5 Core Neil AI Tools (Chatbot · Voice AI · CRM Automation · Smart Scheduler · Email AI)",
        "🎤 AI 1 Voice Agent included",
        "👤 AI Avatar Clone included",
        "🎓 Weekly Training Calls (Wed 7:30 PM) + Setup Guidance",
        "🌐 AI Website Builder – 1 site / month",
        "🎧 AI Music Creator Lite – 3 songs or jingles per month",
        "📣 Social Media AI Assistant – auto-posts & captions",
        "📊 Basic Analytics Dashboard",
        "📬 24/7 Email Support",
        "🧩 Access to Neil AI Community Hub",
        "🤝 White Glove Service Available (additional fee)"
      ],
      tagline: "Launch your AI-powered brand presence in under 7 days.",
      ctaLink: "https://link.fastpaydirect.com/payment-link/690f9c79e92ec1a4b2e5a2f4",
      ctaText: "Get Started",
      featured: false
    },
    {
      name: "⚡ Professional Plan",
      price: "$300",
      period: "mo",
      retainer: "+ $500 retainer",
      subtitle: "Scale Smarter with Full AI Power",
      badge: "Perfect for fast-growing businesses",
      features: [
        "🧠 Full Suite of Neil AI Tools (Website + App + Marketing Studio)",
        "🎤 Unlimited Voice AI Agents",
        "👤 AI Avatar Clone included",
        "🎓 Weekly Training Calls (Wed 7:30 PM) + Priority Setup Support",
        "🌍 Unlimited Websites + Apps",
        "🎧 AI Music Studio – Unlimited songs, soundtracks & brand audio",
        "📣 AI Marketing Automation Suite – ads, posts & lead nurturing",
        "🧩 Custom AI Brand Assistant (voice, logo, tone)",
        "💬 Priority Chat + Email Support",
        "📈 Advanced Analytics + Growth Dashboard",
        "🕒 Monthly Strategy Session with Neil AI Team",
        "🤝 White Glove Service Available (additional fee)"
      ],
      tagline: "Your business fully automated — sales, marketing & creativity on autopilot.",
      ctaLink: "https://link.fastpaydirect.com/payment-link/690f9a8de92ec17499e5a06c",
      ctaText: "Upgrade Now",
      featured: true
    },
    {
      name: "🏆 Enterprise Plan",
      price: "$850",
      period: "mo",
      retainer: "+ $999.95 retainer",
      subtitle: "Go Fully Digital. Dominate Your Market.",
      features: [
        "🚀 Unlimited Everything – websites, apps, campaigns & projects",
        "🎤 Unlimited Voice AI Agents",
        "👤 AI Avatar Clone included",
        "🎓 Weekly Training Calls (Wed 7:30 PM) + VIP Setup Support",
        "🧩 Dedicated Account Manager + Private Slack Channel",
        "🧠 Custom AI Integrations (CRM, API, payment, scheduling, databases)",
        "🎨 White-Label AI Platform – rebrand Neil AI as your own software",
        "🎧 Full AI Music + Audio Branding Suite (with commercial licenses)",
        "📲 Neil AI App Studio – build & launch mobile apps (iOS & Android)",
        "📈 Quarterly AI Growth Audit + ROI Blueprint",
        "💬 24/7 Dedicated Support (chat · email · phone)",
        "💡 Exclusive Beta Access to new Neil AI features",
        "🤝 White Glove Service Available (additional fee)"
      ],
      tagline: "Turn your company into an AI-powered enterprise.",
      ctaLink: "https://link.fastpaydirect.com/payment-link/690f9749e92ec10b66e59c1f",
      ctaText: "Buy Now",
      featured: false,
      gradient: true
    },
    {
      name: "🏢 Corporate Automation Plan",
      price: "$1,200",
      period: "mo",
      retainer: "+ $4,000 retainer",
      badge: "For 10+ Employees or Multiple Locations",
      subtitle: "For Major Corporations",
      features: [
        "💼 Unlimited Calls · Messages · Workflows",
        "🧠 Unlimited AI Agents across Sales, Support, HR & Operations",
        "🎤 Unlimited Voice AI Agents",
        "👤 AI Avatar Clone included",
        "🎓 Weekly Training Calls (Wed 7:30 PM) + Enterprise Setup Team",
        "⚙️ Custom Integrations & Enterprise API Access",
        "📊 AI Analytics & Executive Dashboard",
        "🔐 Advanced Security (SSO, Audit Logs, Role-Based Access)",
        "🧩 White-Label Branding + Multi-Brand Management",
        "👥 Dedicated Onboarding Manager · Monthly Strategy Sessions",
        "🕓 24-Hour SLA Enterprise Support",
        "🤝 White Glove Service Available (additional fee)"
      ],
      tagline: "Ideal for multi-location organizations needing unlimited automation capacity.",
      ctaText: "Talk to Sales",
      ctaLink: "/book",
      featured: false,
      elite: true
    },
    {
      name: "💎 Elite Partnership Plan",
      price: "Custom",
      period: "Starts at $5K/mo",
      subtitle: "The Ultimate Enterprise AI Solution for Global Brands & Government Contracts",
      badge: "Ultimate Enterprise Solution",
      features: [
        "🌐 Private AI Cloud Instance + Dedicated Server Security",
        "🧩 Custom LLM Training on Company Data & Voice Models",
        "🎤 Unlimited Voice AI Agents",
        "👤 AI Avatar Clone included",
        "🎓 Weekly Training Calls (Wed 7:30 PM) + Executive Setup Team",
        "💬 Multilingual Agents for Global Operations",
        "🧠 AI Strategy Consulting with Neil AI Leadership Team",
        "🧾 Legal & Compliance Support (HIPAA / FERPA / Gov Contracts)",
        "🧱 Advanced Integrations with ERP · SAP · Oracle · Salesforce",
        "💼 Private Quarterly Innovation Lab for your Brand",
        "🚀 Guaranteed ROI & Co-Branded Enterprise Showcase",
        "🤝 Full White Glove Service Options Available"
      ],
      tagline: "Built for industry leaders and visionaries driving automation at scale.",
      ctaText: "Request Private Consult",
      ctaLink: "/book",
      featured: false,
      elite: true,
      premium: true
    }
  ];

  return (
    <section id="pricing" className="py-16 space-y-8">
      <div className="text-center space-y-4">
        <h2 className="text-3xl md:text-4xl font-bold text-foreground">
          💼 Neil AI Agentic Automation Plans
        </h2>
        <p className="text-muted-foreground text-base max-w-3xl mx-auto">
          Transform your business with intelligent automation across every department
        </p>
      </div>

      {/* Training & Support Notice */}
      <div className="max-w-4xl mx-auto p-6 border-2 border-primary/30 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-3xl">🎓</span>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-foreground mb-2">We Give You the Tools & Teach You How to Use Them</h3>
            <div className="space-y-2 text-sm text-foreground/90">
              <p>✅ <strong>Set up your own AI agents</strong> with our comprehensive training</p>
              <p>📅 <strong>Weekly team calls every Wednesday at 7:30 PM</strong> — learn how to maximize your AI tools</p>
              <p>🤝 <strong>White Glove Service available</strong> — we can do it all for you for an additional fee</p>
            </div>
            <Button asChild className="mt-4" variant="default">
              <a href="https://link.fastpaydirect.com/payment-link/690fae5cac56fe09acfe0d06">
                Upgrade Today
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* 5-Column Grid - Mobile Optimized */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5 items-stretch">
        {plans.map((plan, index) => (
          <Card 
            key={index}
            className={`relative flex flex-col transition-all duration-300 hover:shadow-xl ${
              plan.featured 
                ? 'border-2 border-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]' 
                : plan.elite 
                ? 'border-2 border-cyan-400 shadow-[0_0_0_4px_rgba(127,255,212,0.15)] bg-gradient-to-b from-card to-card/90' 
                : plan.gradient
                ? 'bg-gradient-to-b from-card to-card/80'
                : 'border-border'
            }`}
          >
            {plan.badge && (
              <Badge className="absolute -top-3 left-3 bg-cyan-400 text-card px-3 py-1 text-xs font-bold border-0">
                {plan.badge}
              </Badge>
            )}
            
            <CardHeader className="space-y-3 pb-4 flex-grow">
              <div>
                <CardTitle className="text-xl mb-2">{plan.name}</CardTitle>
                {plan.subtitle && (
                  <p className="text-sm text-muted-foreground mb-3">{plan.subtitle}</p>
                )}
                <div className="flex items-baseline gap-1.5 mb-1">
                  <span className="text-3xl font-bold text-primary">{plan.price}</span>
                  <span className="text-muted-foreground text-sm">/{plan.period}</span>
                </div>
                {plan.retainer && (
                  <div className="mt-2 p-2 bg-primary/10 border border-primary/20 rounded-md">
                    <p className="text-xs font-semibold text-foreground mb-1">💳 One-Time Setup Fee:</p>
                    <p className="text-sm font-bold text-primary">{plan.retainer.replace('+ ', '').replace(' retainer', '')}</p>
                    <p className="text-xs text-muted-foreground mt-1">Charged at signup, includes full onboarding</p>
                  </div>
                )}
              </div>
              
              <ul className="space-y-2.5 text-sm text-muted-foreground leading-relaxed">
                {plan.features.map((feature, i) => (
                  <li key={i} className={feature.startsWith("Unlimited") || feature.includes("🚀") || feature.includes("💼") ? "font-semibold text-foreground" : ""}>
                    {feature}
                  </li>
                ))}
              </ul>
              
              {plan.tagline && (
                <p className="text-xs text-foreground/80 italic pt-2 font-medium">{plan.tagline}</p>
              )}
            </CardHeader>
            
            <CardContent className="px-6 pb-6 pt-0">
              <Button 
                asChild 
                className={`w-full font-bold ${
                  plan.premium
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white'
                    : plan.elite 
                    ? 'bg-cyan-400 hover:bg-cyan-500 text-card' 
                    : 'bg-primary hover:bg-primary/90 text-primary-foreground'
                }`}
              >
                <a href={plan.ctaLink}>
                  {plan.ctaText || "Get Started"}
                </a>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add-ons Section */}
      <div className="mt-12 p-6 border border-border rounded-xl bg-card/50">
        <h3 className="text-xl font-bold mb-4 text-foreground">🔧 Add-Ons (Available for Any Plan)</h3>
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="p-4 border border-border rounded-lg bg-card">
            <h4 className="font-semibold text-foreground mb-2">🎥 Sora-2 AI Video Creator</h4>
            <p className="text-xs text-muted-foreground mb-2">We create videos for you — up to 30 videos/month</p>
            <p className="text-lg font-bold text-primary mb-2">$135/mo</p>
            <div className="flex gap-2">
              <Button asChild size="sm" className="flex-1">
                <a href="/sora-2-video">Learn More</a>
              </Button>
              <Button asChild size="sm" variant="secondary" className="flex-1">
                <a href="https://link.fastpaydirect.com/payment-link/68938782ddc6a61a45c5d90a">Add</a>
              </Button>
            </div>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <h4 className="font-semibold text-foreground mb-2">📈 AI Social Campaign Accelerator</h4>
            <p className="text-xs text-muted-foreground mb-2">Turn your social media into a 24/7 lead generation engine</p>
            <p className="text-lg font-bold text-primary mb-2">$59/mo</p>
            <div className="flex gap-2">
              <Button asChild size="sm" className="flex-1">
                <a href="/ai-social-campaign">Learn More</a>
              </Button>
              <Button asChild size="sm" variant="secondary" className="flex-1">
                <a href="https://link.fastpaydirect.com/payment-link/68938782ddc6a61a45c5d90a">Add</a>
              </Button>
            </div>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <h4 className="font-semibold text-foreground mb-2">🧠 Custom Domain AI Portal</h4>
            <p className="text-lg font-bold text-primary mb-2">$29/mo</p>
            <Button asChild size="sm" className="w-full">
              <a href="https://link.fastpaydirect.com/payment-link/68938782ddc6a61a45c5d90a">Add to Plan</a>
            </Button>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <h4 className="font-semibold text-foreground mb-2">🎤 Voice Branding Studio</h4>
            <p className="text-lg font-bold text-primary mb-2">$39/mo</p>
            <Button asChild size="sm" className="w-full">
              <a href="https://link.fastpaydirect.com/payment-link/68938782ddc6a61a45c5d90a">Add to Plan</a>
            </Button>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <h4 className="font-semibold text-foreground mb-2">🪄 AI Funnel Builder</h4>
            <p className="text-lg font-bold text-primary mb-2">$99/mo</p>
            <Badge className="text-xs mb-2">Beta</Badge>
            <Button asChild size="sm" className="w-full">
              <a href="https://link.fastpaydirect.com/payment-link/68938782ddc6a61a45c5d90a">Add to Plan</a>
            </Button>
          </div>
        </div>
      </div>

      <div className="text-center space-y-3 pt-6 max-w-4xl mx-auto">
        <p className="text-sm text-muted-foreground/85">
          All plans include comprehensive automation tools. Add-ons enhance your capabilities with specialized features.
        </p>
        <div className="p-4 border border-border/50 rounded-lg bg-card/80 text-left">
          <h4 className="text-sm font-bold text-foreground mb-2">📋 Pricing Terms & Transparency:</h4>
          <ul className="text-xs text-muted-foreground space-y-1.5">
            <li>✅ <strong>One-Time Setup Fee:</strong> Charged at signup (shown in each plan above). Includes full system setup, training access, and onboarding support.</li>
            <li>✅ <strong>Monthly Subscription:</strong> Recurring monthly charge for ongoing service, tool access, and support.</li>
            <li>✅ <strong>No Hidden Fees:</strong> What you see is what you pay. White Glove Service and custom integrations are optional add-ons discussed separately.</li>
            <li>✅ <strong>Cancel Anytime:</strong> Monthly plans can be cancelled. Setup fees are non-refundable after onboarding begins.</li>
            <li>✅ <strong>Data & Privacy:</strong> All user data is encrypted and handled in compliance with privacy regulations. By signing up, you consent to our data processing practices outlined in our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
